import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowRepeat,
  BoxArrowRight,
  Check2,
  ChevronBarContract,
  Diagram2,
  Link45deg,
  Search,
  VectorPen,
  X,
} from "react-bootstrap-icons";
import CatalogTree, { type CatalogNode } from "../components/CatalogTree";
import DiagramEditor, { type DiagramEditorHandle } from "../components/DiagramEditor";
import {
  diagramKindOf,
  withExtension,
  KIND_LABEL,
  type DiagramKind,
} from "../lib/diagramKind";
import {
  FREEFORM_TEMPLATES,
  freeFormTemplateScene,
  type FreeFormTemplateId,
} from "../lib/freeformTemplates";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { useDialogs } from "../components/ui/dialogs";
import { useRealtime } from "../realtime/RealtimeProvider";
import { fetchCatalogs } from "../api";
import { cn } from "../lib/cn";

/**
 * Excalidraw is a large dependency and most sessions never open a free-form
 * diagram, so it is only fetched when one is actually selected.
 */
const FreeFormEditor = lazy(() => import("../components/FreeFormEditor"));

/** Both editors expose the same handle, so one ref type covers them. */
type EditorHandle = DiagramEditorHandle;

type Props = { token: string; username: string; role: string; onLogout: () => void };

type FolderInfo = { path: string; name: string };

const API_URL = "";

const SIDEBAR_DEFAULT_WIDTH = 300;
const SIDEBAR_MIN_WIDTH = 220;
const SIDEBAR_MAX_WIDTH = 760;
const SIDEBAR_WIDTH_KEY = "catalogSidebarWidth";

/**
 * Keeps the panel within its own bounds and always leaves room for the editor,
 * so a wide panel saved on a large monitor can't swallow the canvas on a laptop.
 */
function clampSidebarWidth(value: number) {
  const viewportMax = Math.max(
    SIDEBAR_MIN_WIDTH,
    (typeof window === "undefined" ? Infinity : window.innerWidth) - 360,
  );
  const max = Math.min(SIDEBAR_MAX_WIDTH, viewportMax);
  return Math.round(Math.min(Math.max(value, SIDEBAR_MIN_WIDTH), max));
}

const MINIMAL_DIAGRAM_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  id="Definitions_1">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="250" y="150" width="36" height="36"/>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const getFolders = (nodes: CatalogNode[], parentPath = ""): FolderInfo[] => {
  let folders: FolderInfo[] = [];
  if (parentPath === "") folders.push({ path: ".", name: "Catalog root" });

  for (const node of nodes) {
    const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;
    if (node.type === "folder") {
      folders.push({ path: currentPath, name: currentPath });
      if (node.children) folders = folders.concat(getFolders(node.children, currentPath));
    }
  }
  return folders;
};

const checkFileCanEdit = (
  nodes: CatalogNode[],
  targetPath: string,
  parentPath = "",
): boolean => {
  for (const node of nodes) {
    const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;
    if (currentPath === targetPath) return !!node.canEdit;
    if (node.type === "folder" && targetPath.startsWith(currentPath + "/")) {
      return checkFileCanEdit(node.children || [], targetPath, currentPath);
    }
  }
  return false;
};

const getFilteredCatalog = (nodes: CatalogNode[], query: string): CatalogNode[] => {
  if (!query) return nodes;
  const lowerQuery = query.toLowerCase();

  return nodes.reduce<CatalogNode[]>((acc, node) => {
    if (node.type === "file") {
      if (node.name.toLowerCase().includes(lowerQuery)) acc.push(node);
    } else {
      const filteredChildren = getFilteredCatalog(node.children || [], query);
      if (filteredChildren.length > 0 || node.name.toLowerCase().includes(lowerQuery)) {
        acc.push({ ...node, children: filteredChildren });
      }
    }
    return acc;
  }, []);
};

const countDiagrams = (nodes: CatalogNode[]): number =>
  nodes.reduce(
    (total, node) =>
      total + (node.type === "file" ? 1 : countDiagrams(node.children ?? [])),
    0,
  );

export default function Dashboard({ token, username, role, onLogout }: Props) {
  const dialogs = useDialogs();
  const realtime = useRealtime();

  const [catalog, setCatalog] = useState<CatalogNode[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  // Panel width is restored synchronously so the layout never flashes at the
  // default width before settling on the user's.
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
      const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
      return Number.isFinite(parsed)
        ? clampSidebarWidth(parsed)
        : SIDEBAR_DEFAULT_WIDTH;
    } catch {
      return SIDEBAR_DEFAULT_WIDTH;
    }
  });
  const [isResizing, setIsResizing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDiagramName, setNewDiagramName] = useState("");
  const [newDiagramKind, setNewDiagramKind] = useState<DiagramKind>("bpmn");
  const [newDiagramTemplate, setNewDiagramTemplate] =
    useState<FreeFormTemplateId>("blank");
  const [selectedFolder, setSelectedFolder] = useState(".");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const diagramEditorRef = useRef<EditorHandle | null>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  const isAdmin = role === "admin";

  // Persisted on settle rather than on every pointermove, which would mean a
  // localStorage write per frame while dragging.
  useEffect(() => {
    if (isResizing) return;
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
    } catch {
      /* private mode or blocked storage -- the width just won't persist */
    }
  }, [sidebarWidth, isResizing]);

  // While dragging, hold the resize cursor across the whole document and stop
  // the pointer from selecting text as it sweeps over the tree.
  useEffect(() => {
    if (!isResizing) return;
    const { style } = document.body;
    const previousCursor = style.cursor;
    const previousUserSelect = style.userSelect;
    style.cursor = "col-resize";
    style.userSelect = "none";
    return () => {
      style.cursor = previousCursor;
      style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  // Re-clamp when the window shrinks, so a width saved on a wide screen can't
  // leave the editor with no room.
  useEffect(() => {
    const onResize = () => setSidebarWidth((w) => clampSidebarWidth(w));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const refetchCatalogs = useCallback(() => {
    fetchCatalogs(token).then(setCatalog).catch(console.error);
  }, [token]);

  useEffect(() => {
    refetchCatalogs();
  }, [refetchCatalogs]);

  // Live catalog: anyone creating, deleting, renaming or moving an entry
  // broadcasts, and every open client refetches. Coalesced because a multi-file
  // upload fires one broadcast per file.
  useEffect(() => {
    let timer: number | null = null;
    const unsubscribe = realtime.onCatalogChange(() => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        refetchCatalogs();
      }, 250);
    });
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      unsubscribe();
    };
  }, [realtime, refetchCatalogs]);

  /* --- Folder expansion state (persisted) -------------------------------- */

  const [openPaths, setOpenPaths] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem("catalogOpenPaths");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("catalogOpenPaths", JSON.stringify(openPaths));
  }, [openPaths]);

  const handleToggleFolder = useCallback((path: string, isOpen: boolean) => {
    setOpenPaths((prev) => {
      if (isOpen) return prev.includes(path) ? prev : [...prev, path];
      return prev.filter((p) => p !== path);
    });
  }, []);

  // Collapsing everything also clears the search, because the search
  // auto-expands folders containing matches -- leaving it active would let the
  // next keystroke immediately undo the collapse.
  const handleCollapseAll = useCallback(() => {
    setOpenPaths([]);
    setSearchQuery("");
  }, []);

  /* --- Selection + deep linking ------------------------------------------ */

  const handleSelectFile = useCallback(
    (path: string) => {
      setSelectedFile(path);
      setSearchParams({ file: path });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const fileFromUrl = searchParams.get("file");
    if (!fileFromUrl || fileFromUrl === selectedFile) return;

    setSelectedFile(fileFromUrl);

    // Expand every ancestor so a deep-linked diagram is visible in the tree.
    const parts = fileFromUrl.split("/");
    const pathsToOpen: string[] = [];
    let currentPath = "";
    for (let i = 0; i < parts.length - 1; i += 1) {
      currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
      pathsToOpen.push(currentPath);
    }

    if (pathsToOpen.length > 0) {
      setOpenPaths((prev) => Array.from(new Set([...prev, ...pathsToOpen])));
    }
  }, [searchParams, selectedFile]);

  const handleRenamePath = useCallback(
    (oldPath: string, newPath: string, isFolder: boolean) => {
      setOpenPaths((prev) =>
        prev.map((p) =>
          p === oldPath || p.startsWith(oldPath + "/") ? p.replace(oldPath, newPath) : p,
        ),
      );

      if (
        isFolder &&
        selectedFile &&
        (selectedFile === oldPath || selectedFile.startsWith(oldPath + "/"))
      ) {
        handleSelectFile(selectedFile.replace(oldPath, newPath));
      }
    },
    [selectedFile, handleSelectFile],
  );

  /* --- Search ------------------------------------------------------------- */

  const filteredCatalog = useMemo(
    () => getFilteredCatalog(catalog, searchQuery),
    [catalog, searchQuery],
  );

  // Auto-expand folders that contain matches so results aren't hidden.
  useEffect(() => {
    if (!searchQuery) return;

    const extractFolderPaths = (nodes: CatalogNode[], parent = ""): string[] => {
      const paths: string[] = [];
      nodes.forEach((n) => {
        if (n.type === "folder") {
          const p = parent ? `${parent}/${n.name}` : n.name;
          paths.push(p, ...extractFolderPaths(n.children || [], p));
        }
      });
      return paths;
    };

    const foldersToOpen = extractFolderPaths(getFilteredCatalog(catalog, searchQuery));
    setOpenPaths((prev) => Array.from(new Set([...prev, ...foldersToOpen])));
  }, [searchQuery, catalog]);

  /* --- Actions ------------------------------------------------------------ */

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      await dialogs.notify({
        title: "Could not copy the link",
        description: "Your browser blocked clipboard access. Copy the URL from the address bar instead.",
        tone: "danger",
      });
    }
  };

  const handleSyncToAzDO = async () => {
    const confirmed = await dialogs.confirm({
      title: "Sync approved diagrams to Azure DevOps?",
      description:
        "Every approved diagram in the catalog will be pushed to Azure DevOps. This runs in the background and may take a few minutes.",
      confirmLabel: "Start sync",
    });
    if (!confirmed) return;

    setIsSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/azdo-sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("The server rejected the sync request.");

      await dialogs.notify({
        title: "Synchronization started",
        description: "The sync is running in the background.",
      });
    } catch (error) {
      await dialogs.notify({
        title: "Could not start synchronization",
        description: error instanceof Error ? error.message : "Please try again.",
        tone: "danger",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const folderList = useMemo(() => getFolders(catalog), [catalog]);

  const openCreateModal = (kind: DiagramKind) => {
    setNewDiagramKind(kind);
    setNewDiagramTemplate("blank");
    setNewDiagramName("");
    setCreateError(null);
    setShowCreateModal(true);
  };

  const handleCreateDiagram = async () => {
    if (!newDiagramName.trim()) {
      setCreateError("Please enter a diagram name.");
      return;
    }
    setCreateError(null);

    const filename = withExtension(newDiagramName.trim(), newDiagramKind);
    const fullPath = selectedFolder === "." ? filename : `${selectedFolder}/${filename}`;

    const isFreeForm = newDiagramKind === "freeform";

    try {
      const res = await fetch(`${API_URL}/api/files/${fullPath}`, {
        method: "PUT",
        headers: {
          "Content-Type": isFreeForm ? "application/json" : "application/xml",
          Authorization: `Bearer ${token}`,
        },
        body: isFreeForm
          ? freeFormTemplateScene(newDiagramTemplate)
          : MINIMAL_DIAGRAM_XML,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create diagram on server.");
      }

      setShowCreateModal(false);
      setNewDiagramName("");
      refetchCatalogs();
      if (selectedFolder !== ".") handleToggleFolder(selectedFolder, true);
      handleSelectFile(fullPath);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "An unknown error occurred.");
    }
  };

  /* --- Layout ------------------------------------------------------------- */

  const getFileName = (path: string) => path.split("/").pop() || path;
  const getFolderPath = (path: string) =>
    path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";

  // The sidebar animates its width, and DiagramEditor already watches its own
  // container with a ResizeObserver, so the canvas keeps up frame by frame.
  // This fires one final authoritative resize when the animation settles --
  // replacing the previous fixed 320ms setTimeout guess.
  const handleSidebarTransitionEnd = (event: React.TransitionEvent) => {
    if (event.propertyName === "width") diagramEditorRef.current?.resizeCanvas();
  };

  /* --- Resizable catalog panel ------------------------------------------- */

  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    // Pointer capture keeps the drag alive once the pointer leaves the handle,
    // which it does immediately on any real drag.
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
  };

  const handleResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return;
    // Measuring from the panel's own left edge keeps this correct regardless of
    // what sits to the left of it.
    const left = asideRef.current?.getBoundingClientRect().left ?? 0;
    setSidebarWidth(clampSidebarWidth(event.clientX - left));
  };

  const endResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsResizing(false);
    // No CSS transition runs during a drag, so transitionend never fires here.
    diagramEditorRef.current?.resizeCanvas();
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 48 : 16;

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setSidebarWidth((w) => clampSidebarWidth(w - step));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setSidebarWidth((w) => clampSidebarWidth(w + step));
    } else if (event.key === "Home") {
      event.preventDefault();
      setSidebarWidth(clampSidebarWidth(SIDEBAR_MIN_WIDTH));
    } else if (event.key === "End") {
      event.preventDefault();
      setSidebarWidth(clampSidebarWidth(SIDEBAR_MAX_WIDTH));
    } else if (event.key === "Enter") {
      event.preventDefault();
      setSidebarWidth(clampSidebarWidth(SIDEBAR_DEFAULT_WIDTH));
    }
  };

  const diagramCount = useMemo(() => countDiagrams(catalog), [catalog]);
  const canEditSelected = isAdmin || checkFileCanEdit(catalog, selectedFile);
  const selectedKind = selectedFile ? diagramKindOf(selectedFile) : null;

  return (
    <div className="bg-canvas flex h-full overflow-hidden">
      {/* ---------------------------------------------------------------- */}
      {/* Sidebar                                                           */}
      {/* ---------------------------------------------------------------- */}
      <aside
        ref={asideRef}
        onTransitionEnd={handleSidebarTransitionEnd}
        style={{ width: isSidebarVisible ? sidebarWidth : 0 }}
        className={cn(
          "bg-surface border-line relative z-10 shrink-0 overflow-hidden border-e",
          // The width transition is for collapse/expand only. Leaving it on
          // during a drag makes the panel lag a frame behind the pointer.
          !isResizing &&
            "transition-[width] duration-(--duration-slow) ease-(--ease-standard)",
        )}
      >
        {/* Inner width is pinned to the panel's expanded width so the contents
            don't reflow while collapsing to zero. */}
        <div
          style={{ width: sidebarWidth }}
          className="flex h-full flex-col overflow-hidden"
        >
          <header className="border-line flex h-12 shrink-0 items-center justify-between border-b px-3">
            <div className="flex min-w-0 items-center gap-2">
              <h2 className="text-ink text-sm font-semibold">Catalog</h2>
              {diagramCount > 0 && (
                <span className="bg-surface-sunken text-ink-subtle rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums">
                  {diagramCount}
                </span>
              )}
            </div>

            <div className="flex items-center gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                title={
                  openPaths.length === 0
                    ? "All folders are already collapsed"
                    : "Collapse all folders"
                }
                aria-label="Collapse all folders"
                disabled={openPaths.length === 0}
                onClick={handleCollapseAll}
              >
                <ChevronBarContract size={14} />
              </Button>

              {isAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  iconOnly
                  title="Sync approved diagrams to Azure DevOps"
                  aria-label="Sync to Azure DevOps"
                  disabled={isSyncing}
                  onClick={handleSyncToAzDO}
                >
                  <ArrowRepeat size={14} className={cn(isSyncing && "animate-spin")} />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
                onClick={() => setIsSidebarVisible(false)}
              >
                <X size={18} />
              </Button>
            </div>
          </header>

          {/* Search */}
          <div className="shrink-0 px-3 py-2.5">
            <div className="group relative">
              <Search
                size={13}
                aria-hidden="true"
                className="text-ink-subtle group-focus-within:text-accent pointer-events-none absolute inset-y-0 start-2.5 my-auto transition-colors duration-(--duration-instant)"
              />
              <input
                type="search"
                value={searchQuery}
                placeholder="Search diagrams"
                aria-label="Search diagrams"
                onChange={(event) => setSearchQuery(event.target.value)}
                className={cn(
                  "bg-surface-sunken border-line text-ink placeholder:text-ink-subtle",
                  "h-8 w-full rounded-lg border ps-7.5 pe-7 text-[13px]",
                  "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                  "focus:border-accent focus:bg-surface focus:outline-none",
                  "[&::-webkit-search-cancel-button]:hidden",
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery("")}
                  className="text-ink-subtle hover:text-ink absolute inset-y-0 end-2 my-auto flex h-4 w-4 items-center justify-center"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Tree — the only scrolling region in the sidebar */}
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pb-3">
            <CatalogTree
              catalog={filteredCatalog}
              onSelectFile={handleSelectFile}
              selectedFile={selectedFile}
              isAdmin={isAdmin}
              role={role}
              onCatalogUpdate={refetchCatalogs}
              apiUrl={API_URL}
              token={token}
              openPaths={openPaths}
              onToggleFolder={handleToggleFolder}
              onRenamePath={handleRenamePath}
              searchQuery={searchQuery}
            />
          </div>

          {/* Account */}
          <footer className="border-line bg-surface-sunken flex shrink-0 items-center gap-2.5 border-t px-3 py-2.5">
            <div
              aria-hidden="true"
              className="bg-accent flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            >
              {username.slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="text-ink truncate text-[13px] font-medium">{username}</p>
              <p className="text-ink-subtle text-[11px] capitalize">{role}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              title="Log out"
              aria-label="Log out"
              onClick={onLogout}
            >
              <BoxArrowRight size={14} />
            </Button>
          </footer>
        </div>

        {/* Resize handle. Rendered only while the panel is open, so collapsing
            stays purely the header button's job. */}
        {isSidebarVisible && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize catalog panel"
            aria-valuenow={sidebarWidth}
            aria-valuemin={SIDEBAR_MIN_WIDTH}
            aria-valuemax={SIDEBAR_MAX_WIDTH}
            tabIndex={0}
            title="Drag to resize · double-click to reset"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
            onKeyDown={handleResizeKeyDown}
            onDoubleClick={() => setSidebarWidth(clampSidebarWidth(SIDEBAR_DEFAULT_WIDTH))}
            className={cn(
              "group/resize absolute inset-y-0 end-0 z-20 w-1.5",
              // touch-none stops the browser claiming the gesture for scrolling.
              "cursor-col-resize touch-none select-none",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "absolute inset-y-0 end-0 w-0.5",
                "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                isResizing
                  ? "bg-accent"
                  : "bg-transparent group-hover/resize:bg-accent-ring group-focus-visible/resize:bg-accent",
              )}
            />
          </div>
        )}
      </aside>

      {/* ---------------------------------------------------------------- */}
      {/* Main                                                              */}
      {/* ---------------------------------------------------------------- */}
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {selectedFile ? (
          <>
            <header className="bg-surface border-line flex h-12 shrink-0 items-center justify-between gap-3 border-b px-3">
              <div className="flex min-w-0 items-center gap-2.5">
                {!isSidebarVisible && (
                  <Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    title="Show catalog"
                    aria-label="Show catalog"
                    onClick={() => setIsSidebarVisible(true)}
                  >
                    <Diagram2 size={15} />
                  </Button>
                )}

                <div className="min-w-0 leading-tight">
                  <h1 className="text-ink truncate text-sm font-semibold">
                    {getFileName(selectedFile)}
                  </h1>
                  <p className="text-ink-subtle truncate text-[11px]">
                    {getFolderPath(selectedFile) || "Catalog root"}
                    {!canEditSelected && (
                      <span className="text-ink-muted ms-1.5 font-medium">
                        · Read only
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <Button
                size="sm"
                variant={linkCopied ? "success" : "secondary"}
                onClick={handleCopyLink}
                title="Copy a direct link to this diagram"
              >
                {linkCopied ? <Check2 size={14} /> : <Link45deg size={14} />}
                {linkCopied ? "Copied" : "Share"}
              </Button>
            </header>

            <div className="relative min-h-0 flex-1">
              {/* The extension decides the engine. Keyed on the path so
                  switching diagrams tears the old editor down rather than
                  trying to re-import a scene into a live instance. */}
              {selectedKind === "freeform" ? (
                <Suspense
                  fallback={
                    <div className="text-ink-subtle flex h-full items-center justify-center gap-2.5 text-sm">
                      <span
                        aria-hidden="true"
                        className="border-line border-t-accent h-4 w-4 animate-spin rounded-full border-2"
                      />
                      Loading the free-form editor&hellip;
                    </div>
                  }
                >
                  <FreeFormEditor
                    key={selectedFile}
                    ref={diagramEditorRef}
                    fileId={selectedFile}
                    token={token}
                    readOnly={!canEditSelected}
                    apiUrl={API_URL}
                  />
                </Suspense>
              ) : selectedKind === "bpmn" ? (
                <DiagramEditor
                  key={selectedFile}
                  ref={diagramEditorRef}
                  fileId={selectedFile}
                  token={token}
                  readOnly={!canEditSelected}
                  apiUrl={API_URL}
                />
              ) : (
                <div className="flex h-full items-center justify-center p-6 text-center">
                  <div className="animate-rise-in max-w-sm">
                    <h2 className="text-ink text-base font-semibold">
                      No editor for this file
                    </h2>
                    <p className="text-ink-muted mt-1.5 text-sm leading-relaxed">
                      SCHEmunda opens .bpmn and .excalidraw files. This one is listed
                      in the catalog but cannot be edited here.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="relative flex h-full items-center justify-center p-6">
            {!isSidebarVisible && (
              <Button
                size="sm"
                variant="secondary"
                className="absolute start-3 top-3"
                onClick={() => setIsSidebarVisible(true)}
              >
                <Diagram2 size={14} />
                Show catalog
              </Button>
            )}

            <div className="animate-rise-in max-w-sm text-center">
              <div className="bg-surface border-line shadow-panel mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border">
                <Diagram2 size={24} className="text-ink-subtle" />
              </div>
              <h2 className="text-ink text-base font-semibold">No diagram open</h2>
              <p className="text-ink-muted mt-1.5 text-sm leading-relaxed">
                Pick a diagram from the catalog to start editing, or create a new one.
              </p>
              {isAdmin && (
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <Button
                    size="md"
                    variant="primary"
                    onClick={() => openCreateModal("bpmn")}
                    title="A process diagram, editable with bpmn-js and publishable to Azure DevOps"
                  >
                    <Diagram2 size={14} />
                    New BPMN diagram
                  </Button>
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={() => openCreateModal("freeform")}
                    title="A free-form canvas for architecture sketches, mind maps and retro boards"
                  >
                    <VectorPen size={14} />
                    New free-form diagram
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ---------------------------------------------------------------- */}
      {/* Create diagram                                                    */}
      {/* ---------------------------------------------------------------- */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={`New ${KIND_LABEL[newDiagramKind].toLowerCase()}`}
        description={
          newDiagramKind === "freeform"
            ? "A free canvas for architecture sketches, mind maps and retro boards."
            : "A process diagram, editable with bpmn-js and publishable to Azure DevOps."
        }
        footer={
          <>
            <Button size="md" variant="ghost" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button size="md" variant="primary" onClick={handleCreateDiagram}>
              Create diagram
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreateDiagram();
          }}
        >
          {createError && (
            <p className="bg-danger-soft text-danger animate-fade-in rounded-lg px-3 py-2 text-sm font-medium">
              {createError}
            </p>
          )}

          <div>
            <label
              htmlFor="new-diagram-name"
              className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase"
            >
              Diagram name
            </label>
            <input
              id="new-diagram-name"
              data-autofocus
              type="text"
              value={newDiagramName}
              placeholder={
                newDiagramKind === "freeform" ? "Platform architecture" : "Customer onboarding"
              }
              onChange={(event) => setNewDiagramName(event.target.value)}
              className="border-line bg-surface-sunken text-ink placeholder:text-ink-subtle focus:border-accent focus:bg-surface h-10 w-full rounded-lg border px-3 text-sm transition-colors duration-(--duration-instant) focus:outline-none"
            />
          </div>

          {newDiagramKind === "freeform" && (
            <fieldset className="min-w-0">
              <legend className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase">
                Starting point
              </legend>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {FREEFORM_TEMPLATES.map((template) => {
                  const isSelected = newDiagramTemplate === template.id;
                  return (
                    <label
                      key={template.id}
                      className={cn(
                        "flex cursor-pointer flex-col rounded-lg border px-3 py-2.5",
                        "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                        isSelected
                          ? "border-accent bg-accent-soft"
                          : "border-line bg-surface-sunken hover:bg-surface-hover",
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="freeform-template"
                          value={template.id}
                          checked={isSelected}
                          onChange={() => setNewDiagramTemplate(template.id)}
                          className="accent-accent"
                        />
                        <span
                          className={cn(
                            "text-[13px] font-medium",
                            isSelected ? "text-accent" : "text-ink",
                          )}
                        >
                          {template.label}
                        </span>
                      </span>
                      <span className="text-ink-subtle mt-1 ps-6 text-xs leading-snug">
                        {template.description}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          <div>
            <label
              htmlFor="new-diagram-folder"
              className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase"
            >
              Location
            </label>
            <select
              id="new-diagram-folder"
              value={selectedFolder}
              onChange={(event) => setSelectedFolder(event.target.value)}
              className="border-line bg-surface-sunken text-ink focus:border-accent focus:bg-surface h-10 w-full rounded-lg border px-3 text-sm transition-colors duration-(--duration-instant) focus:outline-none"
            >
              {folderList.map((folder) => (
                <option key={folder.path} value={folder.path}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Modal>
    </div>
  );
}
